import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Users, MapPin, Plus } from 'lucide-react';
import { useAuth } from '../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWNER_ROLES = ['clinic_owner', 'super_admin', 'founder'];

// "X of Y seats/locations used" + Add button when full. kind: 'seats' | 'locations'
export const PlanUsageBar = ({ kind, refreshKey }) => {
  const { user } = useAuth();
  const [u, setU] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/plan-limits/usage`).then((r) => setU(r.data)).catch(() => setU(null));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    window.addEventListener('audinexa:plan-usage-changed', load);
    return () => window.removeEventListener('audinexa:plan-usage-changed', load);
  }, [load]);

  if (!u) return null;
  const isSeat = kind === 'seats';
  const used = isSeat ? u.seats_used : u.locations_used;
  const limit = isSeat ? u.user_limit : u.location_limit;
  const price = isSeat ? u.extra_user_price : u.extra_location_price;
  const full = limit != null && used >= limit;
  const noun = isSeat ? 'seats' : 'locations';
  const Icon = isSeat ? Users : MapPin;

  const openAdd = () => window.dispatchEvent(new CustomEvent('audinexa:plan-limit', { detail: {
    limit_code: isSeat ? 'SEAT_LIMIT_REACHED' : 'LOCATION_LIMIT_REACHED',
    addon: isSeat ? 'extra_user' : 'extra_location', addon_price: price, used, limit, tier: u.tier,
    detail: isSeat
      ? `You've used all ${limit} user seats. Add a user for ₹${price}/month or upgrade your plan.`
      : `You've reached your location limit. Add an extra location for ₹${price}/month or upgrade to Chain / contact us for Enterprise.`,
  } }));

  return (
    <div data-testid={`plan-usage-${kind}`}
      className={`mb-3 flex items-center justify-between gap-2 text-xs rounded px-3 py-2 border ${full ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
      <span className="inline-flex items-center gap-1.5" data-testid={`plan-usage-${kind}-text`}>
        <Icon size={13} />
        {limit == null ? `${used} ${noun} in use` : <><b>{used}</b>&nbsp;of&nbsp;<b>{limit}</b>&nbsp;{noun} used</>}
        {isSeat && u.pending_invites > 0 && <span className="text-slate-400">({u.pending_invites} pending invite{u.pending_invites === 1 ? '' : 's'})</span>}
      </span>
      {full && OWNER_ROLES.includes(user?.role) && (
        <button onClick={openAdd} data-testid={`plan-usage-${kind}-add-btn`}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors">
          <Plus size={11} /> {isSeat ? 'Add seat' : 'Add location'}
        </button>
      )}
    </div>
  );
};
